/**
 * The gate on Closed Won: a won deal has to carry a value.
 *
 * WHY THIS EXISTS
 * ---------------
 * Closing a lead used to record the win and nothing else, so a deal worth ₹73,744
 * contributed to every conversion figure and zero to every revenue figure. The
 * value the reports read is Financials.final_quoted_price, and nothing was
 * writing it — not even freezing a quotation, which sets accepted_plan and stops
 * there. The pipeline read healthy while the revenue report read empty.
 *
 * So Closed Won now asks the one question nobody was asking: how much?
 *
 * TWO ANSWERS, IN ORDER OF TRUTH
 * ------------------------------
 * A frozen quotation is the real answer — it is the plan the client actually
 * agreed to, with a price somebody approved. An estimated value is the fallback
 * for a deal agreed over the phone with no quotation behind it.
 *
 * The quotation list is shown even when there is only one. Freezing is not a
 * formality: it marks the quote accepted, locks the plan, and is the figure that
 * flows into onboarding and the AMC. A rep should see which plan they are
 * committing to before it becomes the number on the report.
 *
 * "None of these" exists because a lead can carry three superseded drafts and a
 * price agreed on a call. Forcing one of the drafts to be frozen would put a
 * wrong number in the reports, which is the problem this screen was built to fix.
 */
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Check, FileText, IndianRupee, Loader2 } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { QUOTATIONS_URL, QUOTATION_DETAIL_URL, QUOTATION_FINALIZE_URL } from "../../constants/api";
import { DialogModal } from "../ui/dialogModal";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../common/ToastMsg";

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

const ESTIMATE = "__estimate__";

export default function CloseWonValueModal({ open, customerId, customerName, onClose, onConfirm }) {
    const { fetch, create } = useApiMethods();

    const [loading, setLoading]   = useState(true);
    const [quotes, setQuotes]     = useState([]);
    const [choice, setChoice]     = useState(null);   // quotation id, or ESTIMATE
    const [plans, setPlans]       = useState([]);     // plans of the chosen quotation
    const [planId, setPlanId]     = useState(null);
    const [plansLoading, setPlansLoading] = useState(false);
    const [estimate, setEstimate] = useState("");
    const [saving, setSaving]     = useState(false);

    /* Load this lead's quotations. An already-accepted one is the answer — no
       freeze needed, and the modal says so rather than offering to re-freeze. */
    useEffect(() => {
        if (!open || !customerId) return;
        setLoading(true);
        setChoice(null); setPlans([]); setPlanId(null); setEstimate("");
        fetch(`${QUOTATIONS_URL}?customer_id=${customerId}`)
            .then(res => {
                const rows = res?.status === "success" ? (res.data || []) : [];
                setQuotes(rows);
                const accepted = rows.find(q => q.status === "accepted" && q.accepted_plan);
                if (accepted) setChoice(accepted.id);
                else if (rows.length === 0) setChoice(ESTIMATE);
            })
            .catch(() => setQuotes([]))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, customerId]);

    const chosen = quotes.find(q => q.id === choice) || null;
    const alreadyFrozen = chosen?.status === "accepted" && chosen?.accepted_plan;

    /* Plans are not on the list payload, so fetch them for the quotation the rep
       picked. Lazy on purpose: a lead with eight quotations should not pull eight
       plan sets to show a chooser. */
    const loadPlans = useCallback((quoteId) => {
        setPlansLoading(true); setPlans([]); setPlanId(null);
        fetch(QUOTATION_DETAIL_URL(quoteId))
            .then(res => {
                const rows = res?.status === "success" ? (res.data?.plans || []) : [];
                setPlans(rows);
                // One plan is not a choice — preselect it. Several: make them pick.
                if (rows.length === 1) setPlanId(rows[0].id);
                else {
                    const rec = rows.find(p => p.is_recommended);
                    if (rec) setPlanId(rec.id);
                }
            })
            .catch(() => setPlans([]))
            .finally(() => setPlansLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pick = (id) => {
        setChoice(id);
        if (id === ESTIMATE) { setPlans([]); setPlanId(null); return; }
        const q = quotes.find(x => x.id === id);
        if (q?.status === "accepted" && q.accepted_plan) { setPlans([]); setPlanId(null); return; }
        loadPlans(id);
    };

    const estimateValue = Number(String(estimate).replace(/,/g, "")) || 0;
    const selectedPlan  = plans.find(p => p.id === planId) || null;

    const canConfirm =
        choice === ESTIMATE ? estimateValue > 0
        : alreadyFrozen     ? true
        : !!selectedPlan;

    const confirm = async () => {
        if (!canConfirm || saving) return;
        setSaving(true);
        try {
            // Estimate: nothing to freeze, just hand the number back.
            if (choice === ESTIMATE) {
                onConfirm({ deal_value: estimateValue, quotation_id: null, source: "estimate" });
                return;
            }

            // Already accepted: the value is settled, do not touch the quote.
            if (alreadyFrozen) {
                onConfirm({
                    deal_value: Number(chosen.accepted_plan.grand_total) || 0,
                    quotation_id: chosen.id,
                    source: "frozen",
                });
                return;
            }

            // Freeze through the existing endpoint rather than reimplementing it —
            // it also blocks a quote whose discount is still awaiting approval,
            // which is exactly the case that should not close as won.
            const res = await create(QUOTATION_FINALIZE_URL(chosen.id), {
                plan_id: planId,
                remarks: `Frozen on marking ${customerName || "this lead"} Closed Won.`,
            });
            if (res?.status !== "success") {
                ToastMsg("error", res?.message || "Could not freeze the quotation");
                return;
            }
            onConfirm({
                deal_value: Number(selectedPlan.grand_total) || 0,
                quotation_id: chosen.id,
                source: "frozen",
            });
        } catch (e) {
            ToastMsg("error", "Could not freeze the quotation");
        } finally {
            setSaving(false);
        }
    };

    return (
        <DialogModal
            open={open}
            onClose={onClose}
            title="What is this deal worth?"
            subtitle={`Closing ${customerName || "this lead"} as won`}
            showActions={false}
            width="620px"
        >
            {loading ? (
                <div className="py-10 grid place-items-center text-sm text-gray-500">
                    <Loader2 className="h-5 w-5 animate-spin mb-2" />
                    Looking for quotations…
                </div>
            ) : (
                <div className="space-y-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Revenue reports read this figure. Without it the win counts in the
                        pipeline and contributes nothing to revenue.
                    </p>

                    {/* ── quotations ────────────────────────────────────── */}
                    {quotes.length > 0 && (
                        <div className="space-y-2">
                            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                Freeze the quotation the client agreed to
                            </p>
                            {quotes.map(q => {
                                const isSel  = choice === q.id;
                                const frozen = q.status === "accepted" && q.accepted_plan;
                                const pending = q.approval_status === "pending";
                                return (
                                    <button
                                        key={q.id} type="button" onClick={() => pick(q.id)}
                                        className={cn(
                                            "w-full text-left rounded-lg border-2 px-3 py-2.5 transition-colors",
                                            isSel
                                                ? "border-emerald-500 bg-emerald-50/60 dark:bg-emerald-900/15"
                                                : "border-gray-200 dark:border-gray-700 hover:border-gray-300",
                                        )}
                                    >
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="flex items-center gap-2 min-w-0">
                                                <FileText className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                                                <span className="text-sm font-medium truncate">{q.quote_number}</span>
                                                {frozen && (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700
                                                                     dark:bg-emerald-900/40 dark:text-emerald-300 shrink-0">
                                                        already frozen
                                                    </span>
                                                )}
                                                {/* Said here rather than on submit: a quote whose discount
                                                    is unapproved cannot be frozen, and finding that out
                                                    after picking it wastes the rep's time. */}
                                                {pending && !frozen && (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700
                                                                     dark:bg-amber-900/40 dark:text-amber-300 shrink-0">
                                                        discount awaiting approval
                                                    </span>
                                                )}
                                            </span>
                                            <span className="text-sm font-semibold tabular-nums shrink-0">
                                                {frozen ? inr(q.accepted_plan.grand_total) : ""}
                                            </span>
                                        </div>

                                        {/* Plan chooser, inline under the quotation it belongs to */}
                                        {isSel && !frozen && (
                                            <div className="mt-2.5 pt-2.5 border-t border-gray-200 dark:border-gray-700">
                                                {plansLoading ? (
                                                    <p className="text-xs text-gray-500 flex items-center gap-1.5">
                                                        <Loader2 className="h-3 w-3 animate-spin" /> Loading plans…
                                                    </p>
                                                ) : plans.length === 0 ? (
                                                    <p className="text-xs text-amber-600">
                                                        This quotation has no plans — it cannot be frozen.
                                                    </p>
                                                ) : (
                                                    <div className="space-y-1.5">
                                                        <p className="text-[11px] text-gray-500">
                                                            {plans.length === 1 ? "Plan on this quotation" : "Which plan did they take?"}
                                                        </p>
                                                        {plans.map(p => (
                                                            <label key={p.id}
                                                                className="flex items-center justify-between gap-3 cursor-pointer text-sm">
                                                                <span className="flex items-center gap-2 min-w-0">
                                                                    <input
                                                                        type="radio" name={`plan-${q.id}`}
                                                                        checked={planId === p.id}
                                                                        onChange={() => setPlanId(p.id)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                    <span className="truncate">
                                                                        {p.plan_label}
                                                                        {p.package_title ? ` — ${p.package_title}` : ""}
                                                                    </span>
                                                                </span>
                                                                <span className="tabular-nums shrink-0">{inr(p.grand_total)}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* ── estimate ──────────────────────────────────────── */}
                    <div className="space-y-2">
                        {quotes.length > 0 ? (
                            <button type="button" onClick={() => pick(ESTIMATE)}
                                className={cn(
                                    "w-full text-left rounded-lg border-2 px-3 py-2.5 text-sm transition-colors",
                                    choice === ESTIMATE
                                        ? "border-emerald-500 bg-emerald-50/60 dark:bg-emerald-900/15"
                                        : "border-dashed border-gray-300 dark:border-gray-600 hover:border-gray-400",
                                )}>
                                None of these — enter the agreed value instead
                            </button>
                        ) : (
                            <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/10 px-3 py-2.5
                                            text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
                                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                <span>
                                    No quotation on this lead. Enter what the client agreed to —
                                    raise a quotation later and the frozen figure takes over.
                                </span>
                            </div>
                        )}

                        {choice === ESTIMATE && (
                            <div>
                                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                                    Estimated deal value <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <IndianRupee className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <Input
                                        type="number" min="1" autoFocus
                                        value={estimate}
                                        onChange={(e) => setEstimate(e.target.value)}
                                        placeholder="Total contract value"
                                        className="pl-8"
                                    />
                                </div>
                                {estimateValue > 0 && (
                                    <p className="text-[11px] text-gray-500 mt-1 tabular-nums">{inr(estimateValue)}</p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ── what will be recorded ─────────────────────────── */}
                    <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-xs text-gray-500">
                            {choice === ESTIMATE && estimateValue > 0 && <>Recording <strong className="text-gray-800 dark:text-gray-200">{inr(estimateValue)}</strong> as an estimate</>}
                            {alreadyFrozen && <>Using <strong className="text-gray-800 dark:text-gray-200">{inr(chosen.accepted_plan.grand_total)}</strong> from {chosen.quote_number}</>}
                            {!alreadyFrozen && choice !== ESTIMATE && selectedPlan && <>Freezing {chosen?.quote_number} at <strong className="text-gray-800 dark:text-gray-200">{inr(selectedPlan.grand_total)}</strong></>}
                            {!canConfirm && <>Pick a quotation and plan, or enter a value</>}
                        </p>
                        <div className="flex items-center gap-2 shrink-0">
                            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                            <Button onClick={confirm} disabled={!canConfirm || saving}>
                                {saving
                                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Freezing…</>
                                    : <><Check className="h-3.5 w-3.5 mr-1.5" /> Confirm</>}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </DialogModal>
    );
}
